<!-- App.vue -->
<template>
  <div class="app">
    <header class="header">
      <div class="header-content">
        <h1>Nos Délices Culinaires</h1>
        <p class="subtitle">
          Découvrez nos plats préparés avec passion pour ravir vos papilles et
          enchanter vos repas
        </p>
      </div>
    </header>

    <div class="container">
      <div class="menu-intro">
        <h2>Nos Spécialités</h2>
        <p>
          Une sélection de nos meilleurs plats, préparés par nos chefs avec des
          ingrédients frais et de saison.
        </p>
      </div>

      <div class="menu-grid">
        <div v-for="(item, index) in menuItems" :key="index" class="menu-item">
          <div class="menu-item-image">
            <img :src="item.image" :alt="item.title" />
          </div>
          <div class="menu-item-content">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <button class="btn" @click="downloadPDF(item.pdf)">Menu</button>
          </div>
        </div>
      </div>
    </div>

    <footer>
      <div class="footer-content">
        <p>
          &copy; {{ new Date().getFullYear() }} La Pizza Du Lac. Tous
          droits réservés.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'
import { restaurantSchema, injectSchema, breadcrumbSchema } from '@/utils/seoConfig'

export default {
  name: "MenuView",
  components: {},
  setup() {
    useHead({
      title: 'Notre Menu - La Pizza du Lac',
      meta: [
        {
          name: 'description',
          content: 'Consultez notre menu complet : pizzas artisanales, burgers grillés, calzones et cocktails. Découvrez nos spécialités préparées avec des ingrédients frais et de saison.'
        },
        {
          property: 'og:title',
          content: 'Notre Menu - La Pizza du Lac'
        },
        {
          property: 'og:description',
          content: 'Pizzas artisanales, burgers, calzones et cocktails. Consultez nos menus en PDF.'
        },
        {
          property: 'og:type',
          content: 'website'
        }
      ]
    })
  },
  mounted() {
    // Injecter les schemas JSON-LD
    injectSchema(restaurantSchema)
    injectSchema(breadcrumbSchema([
      { name: 'Accueil', url: 'https://lapizzadulac.com/' },
      { name: 'Menu', url: 'https://lapizzadulac.com/menu' }
    ]))
  },

  data() {
    return {
      menuItems: [
        {
          title: "Pizzas",
          description:
            "Dégustez nos délicieuses pizzas à base de pâte fraîche au levain, garnies d'ingrédients de première qualité.",
          image: "https://images.unsplash.com/photo-1513104890138-7c749659a591",
          pdf: "Pizza.pdf",
        },
        {
          title: "Burger et Grillade",
          description:
            "Des viandes d'exception sélectionnées pour la qualité de leur provenance et préparées à la perfection sur nos grills à charbon de bois.",
          image: "https://images.unsplash.com/photo-1558030006-450675393462",
          pdf: "BurgerGrillade.pdf",
        },
        {
          title: "Menu Complet 2025",
          description: "Nous vous invitons à découvrir notre carte complète.",
          image: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd",
          pdf: "menu2025.pdf",
        },
      ],
    };
  },
  methods: {
    downloadPDF(filename) {
      const pdfUrl = `/menu/${filename}`;
      window.open(pdfUrl, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
// Variables #f8e6aa
$primary-color: #3f2afd;
$dark-bg: #ffcc85;
$light-bg: #fffbf3;
$text-dark: #333;
$text-light: #666;
$white: #fff;
$transition-default: all 0.3s ease;
$box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
$box-shadow-hover: 0 15px 30px rgba(0, 0, 0, 0.15);
$border-radius: 10px;

// Réinitialisation et styles de base
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Poppins", sans-serif;
}

body {
  background-color: $light-bg;
  color: $text-dark;
  line-height: 1.6;
}

// En-tête
.header {
  background: linear-gradient(135deg, $dark-bg 0%, $dark-bg 100%);
  color: $white;
  padding: 100px 20px 40px 0;
  text-align: center;
  position: relative;

  &-content {
    max-width: 800px;
    margin: 0 auto;
  }

  h1 {
    font-size: 48px;
    margin-bottom: 10px;
    font-weight: 700;
  }

  .subtitle {
    font-size: 18px;
    color: $primary-color;
    font-style: italic;
    margin-bottom: 20px;
  }
}

// Section principale
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

.menu-intro {
  text-align: center;
  margin-bottom: 60px;

  h2 {
    font-size: 32px;
    margin-bottom: 20px;
    position: relative;
    display: inline-block;

    &:after {
      content: "";
      position: absolute;
      width: 60px;
      height: 3px;
      background-color: $primary-color;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  p {
    font-size: 18px;
    max-width: 700px;
    margin: 0 auto;
    color: $text-light;
  }
}

// Grille des plats
.menu-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
}

.menu-item {
  background-color: $white;
  border-radius: $border-radius;
  overflow: hidden;
  box-shadow: $box-shadow;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: $box-shadow-hover;

    .menu-item-image {
      img {
        transform: scale(1.05);
      }
    }
  }

  &-image {
    height: 250px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
    }
  }

  &-content {
    padding: 25px;
  }

  &-number {
    font-size: 14px;
    color: $primary-color;
    margin-bottom: 5px;
  }

  h3 {
    font-size: 24px;
    margin-bottom: 15px;
    color: $text-dark, 5%;
  }

  p {
    color: $text-light;
    margin-bottom: 20px;
    font-size: 16px;
  }
}

.btn {
  display: inline-block;
  background-color: $primary-color;
  color: #fff;
  padding: 12px 25px;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  transition: $transition-default;
  border: 2px solid $primary-color;

  &:hover {
    background-color: transparent;
    color: $primary-color;
  }
}

// Pied de page
footer {
  background-color: $dark-bg;
  color: $white;
  text-align: center;
  padding: 30px 20px;

  .footer-content {
    max-width: 800px;
    margin: 0 auto;
  }
}

// Media queries pour la responsivité
@media (max-width: 768px) {
  .header {
    h1 {
      font-size: 36px;
    }
  }

  .menu-intro {
    h2 {
      font-size: 28px;
    }
  }

  .menu-grid {
    grid-template-columns: 1fr;
  }
}
</style>